/*
  Removes the Auth0 callback secrets from a URL before that URL is copied anywhere it will
  outlive the page. Pure by design and free of posthog-js and @/env, because both the
  analytics layer and the support-mail layer need it and the mail layer must not drag an
  analytics vendor into its dependency graph to get it.

  Two exported functions, because the two callers want different amounts of scrubbing. Both
  strip the OAuth params; only the analytics one collapses UUID path segments. They share one
  implementation so the OAuth rule cannot drift between them.
*/

const OAUTH_SENSITIVE_PARAMS = ['code', 'state']
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/*
  Accepts both full URLs and bare paths.
    1. Strip Auth0 callback params (code, state). The redirect (/?code=…&state=…) lands in
       window.location before the SPA router rewrites it, so anything that reads the location
       early can capture a live authorization code.
    2. Optionally replace UUID path segments with ":id".
  Returns the input string untouched when neither rule fires, so a caller that logs the value
  never sees a URL that differs only by WHATWG normalization.
*/
function scrub(rawUrl: string | undefined, collapseUuidSegments: boolean): string | undefined {
  if (!rawUrl) {
    return rawUrl
  }
  const isPathOnly = rawUrl.startsWith('/')
  try {
    const url = isPathOnly ? new URL(rawUrl, 'http://_scrub') : new URL(rawUrl)
    let mutated = false
    for (const param of OAUTH_SENSITIVE_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param)
        mutated = true
      }
    }
    if (collapseUuidSegments) {
      const segments = url.pathname.split('/')
      let pathChanged = false
      for (let i = 0; i < segments.length; i++) {
        if (UUID_RE.test(segments[i])) {
          segments[i] = ':id'
          pathChanged = true
        }
      }
      if (pathChanged) {
        url.pathname = segments.join('/')
        mutated = true
      }
    }
    if (!mutated) {
      return rawUrl
    }
    return isPathOnly ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  } catch {
    // Fail open: unparseable inputs can't carry valid OAuth params as query
    // strings, and UUIDs aren't secret — better to send the unmodified value
    // than drop the event.
    return rawUrl
  }
}

/*
  For analytics. Collapsing /steam/traps/<uuid>/details to /steam/traps/:id keeps "top pages"
  down to one row per route template instead of one row per asset.
*/
export function scrubUrl(rawUrl: string | undefined): string | undefined {
  return scrub(rawUrl, true)
}

/*
  For anything a human reads about one specific visit, such as the support mail body. The
  asset id in the path is the first thing the person triaging the ticket needs in order to
  open the same page, and it is not a secret — the user already sees it in their address bar.
  Only the OAuth params are removed here.
*/
export function stripOAuthParams(rawUrl: string): string
export function stripOAuthParams(rawUrl: string | undefined): string | undefined
export function stripOAuthParams(rawUrl: string | undefined): string | undefined {
  return scrub(rawUrl, false)
}
